/**
 * The sheet element's step hosts, built from one table.
 *
 * `SheetElementParts` is that table: the element's port (`SheetElementPort`,
 * what its bind, presentation and swap steps reach) plus the few private
 * parts the other step modules read. The element builds it once
 * (`SheetElement`'s `#port`); each function here builds one step module's
 * host from it, read when the step runs, so no step's adapter is written
 * inline in sheet-element.ts. Public members are read through `element`.
 *
 * Kept in the element: its core (`#core`), the size and row-viewport hosts
 * (they read the frame's scroll top), the fixed-children queue's ranking,
 * the presentation machine and the harness door.
 */

import { type Option } from "@tento-lona/sheets";
import type { RowSyncState } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import type { SheetViewModel } from "@tento-lona";
import { nonnull } from "@tento-core/assert";
import type { LiveData } from "@tento-core/livedata";
import { Tracing } from "@tento-core/tracing";
import type { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { DomFlags } from "@tento-ui/dom-flags";
import type { SizeGate } from "@tento-ui/ui/size-gate";
import type { BindSheetWorkHost } from "./sheet-bind-work.ts";
import { LayoutDebugOverlayController } from "./dbg/dbg-layout-overlay-controller.ts";
import type { SheetCellTargets } from "./sheet-cell-targets.ts";
import type { SheetDelegate } from "./sheet-delegate.ts";
import type { DecorationContextHost } from "./sheet-decoration-context.ts";
import type { EmitHandleHost, EmitHandleState } from "./sheet-emit-handle.ts";
import { bindSheet, rebindStructurePass } from "./sheet-element-bind.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";
import type { SheetElementPort } from "./sheet-element-port.ts";
import type { CommittedRowGeometry } from "./sheet-element-state.ts";
import { holdEditorObservers, suppressForViewModelSwap } from "./sheet-element-swap.ts";
import type { SheetElement } from "./sheet-element.ts";
import type { FixedChildHandleHost } from "./sheet-fixed-child-handle.ts";
import type { FixedChildrenQueue } from "./fixed-children-queue.ts";
import type { GeometryRebindHost } from "./sheet-geometry-rebinds.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";
import type { SheetLayoutGate } from "./sheet-layout-gate.ts";
import type { SheetLegend } from "./sheet-legend.ts";
import { type LegendHostingHost, rebindLegend } from "./sheet-legend-hosting.ts";
import type { FixedChildrenLifetime } from "./fixed-children-lifetime.ts";
import type { RebindHandleHost, RebindHandleState } from "./sheet-rebind-handle.ts";
import type { RenderPluginManager, RowOptionsElement } from "./render-plugin.ts";
import type { StateHandleHost } from "./sheet-state-handle.ts";
import type { StaticChildHandleHost } from "./sheet-static-child-handle.ts";

/**
 * The `DomFlags` flag for whether the rendered emit's per-row pass follows
 * RowDemand (tento 20bd8b2aa6). Off by default until a dev ux run with it on
 * is green (a behaviour change in the bind/emit path gets one before it is the
 * default). Off, the emit runs every row's pass, as before 20bd8b2aa6.
 */
const ROW_DEMAND = "row-demand";

/** A layout callback that needs the row list ran with none bound. */
export function assertrl(): void {
  console.trace();
  Tracing.dev("no row list");
}

/** The element's one table: its port plus the private parts the other step hosts read. */
export interface SheetElementParts extends SheetElementPort {
  /** The element itself: its public handles, flags, callbacks and legend widths. */
  readonly element: SheetElement;
  /** The rebind handle's state (written by sheet-rebind-handle.ts only). */
  readonly rebindState: RebindHandleState;
  /** The emit loop's state (written by sheet-emit-handle.ts only). */
  readonly emitState: EmitHandleState;
  /** Row geometry as of the last committed bind (written by sheet-bind-work.ts only). */
  readonly committed: CommittedRowGeometry;
  /** Cell and fixed/static-child target resolution. */
  readonly cellTargets: SheetCellTargets;
  /** The element's size gate. */
  readonly sizeGate: SizeGate;
  /** The column index the scroll is locked at (`lockCurrentIndex`), if any. */
  readonly lockedIndex: Option<number>;
  /** The render plugins `init` installed. */
  readonly pluginManager: Option<RenderPluginManager>;
  /** Whether the debug popover shows (set by `init`). */
  readonly shouldShowDebugPopover: Option<LiveData.Readable<boolean>>;
  /** The composed row-options renderer a host injected, if any. */
  readonly composedOptionsRenderer: Option<RowOptionsElement.ComposedRenderer>;
  /** The row sync-state provider, if set. */
  readonly syncStateProvider: Option<(rowId: RowLocalId) => Option<RowSyncState>>;
  /** The three legends of the element's template. */
  readonly legends: { readonly legend: SheetLegend; readonly headerLegend: SheetLegend; readonly footerLegend: SheetLegend };
  /** The legend host (sheet-legend-hosting.ts). */
  readonly legendHost: LegendHostingHost;
  /** Retires in-flight fixed-children requests on detach, recycle and rebind. */
  readonly fixedChildrenLifetime: FixedChildrenLifetime;
  /** Per-row fixed-children applies under the frame budget. */
  readonly fixedChildrenQueue: FixedChildrenQueue;
  /** The collapsed legend rail's width when `legendCollapsedWidthPx` is null. */
  readonly defaultCollapsedLegendPx: number;
  /** Start a fixed-child request for `key`: returns its generation, superseding earlier ones. */
  nextSpecGeneration(key: string): number;
  /** Whether `gen` is still the latest request generation for `key`. */
  isCurrentSpecGeneration(key: string, gen: number): boolean;
  /** Resolve the uniform cell width from the layout-gate props and bind it. */
  applyResolvedLayout(
    sheet: SheetViewModel.Surface<SheetBinder>,
    layout: SheetLayoutPort,
    props: SheetLayoutGate.Props,
    content: Option<SheetLayoutPort.Content>,
  ): void;
  /** Seed the size gate from a direct measurement when no notification arrived. */
  seedSizeFromMeasurement(): void;
}

/** The cell-target resolver's host (sheet-cell-targets.ts). */
export function cellTargetsHostOf(p: SheetElementParts): SheetCellTargets.Host {
  return {
    get sheet() { return p.core.sheet; },
    get layout() { return p.core.layout; },
    get staticChildHandle() { return p.element.staticChildHandle; },
  };
}

/** What the geometry rebinds read (sheet-geometry-rebinds.ts). */
export function geometryHostOf(p: SheetElementParts): GeometryRebindHost {
  return {
    get sheet() { return p.core.sheet; },
    get layout() { return p.core.layout; },
    get bindGeneration() { return p.bindState.bindGeneration; },
    get activeRowOptions() { return p.activeRowOptions.get(); },
    get rowOptionsAnchor() { return p.rebindState.rowOptionsAnchor; },
    dismissStaticChild: (staticId) => {
      p.$$.root.scrollPort.dismissStaticChild("header", staticId);
      p.$$.root.scrollPort.dismissStaticChild("body", staticId);
    },
    staticChildYPosition: (rowId, rowLayout) => p.cellTargets.staticChildYPosition(rowId, rowLayout),
    bodyFixedChildTop: (offsetPx, location, sheet) => SheetElementLayout.bodyFixedChildTop(offsetPx, location, sheet),
  };
}

/** The bind pass's geometry and content host (sheet-bind-work.ts). */
export function bindSheetWorkHostOf(p: SheetElementParts): BindSheetWorkHost {
  return {
    core: p.core,
    committed: p.committed,
    get lockedIndex() { return p.lockedIndex; },
    applyResolvedLayout: (sheet, layout, props, content) => p.applyResolvedLayout(sheet, layout, props, content),
    get layoutGate() { return p.layoutGate; },
    seedSizeFromMeasurement: () => p.seedSizeFromMeasurement(),
    get sizeGate() { return p.sizeGate; },
    rebindLegend: (sheetChanged) => rebindLegend(p.legendHost, sheetChanged),
    get leftOccludedWidthPx() { return p.element.leftOccludedWidthPx; },
    holdEditorObservers: (signal) => holdEditorObservers(p, signal),
    get root() { return p.$$.root; },
    get shouldShowDebugPopover() { return p.shouldShowDebugPopover; },
    rebindFixedChildYPositions: () => p.rebindFixedChildYPositions(),
  };
}

/** The legend column's host (sheet-legend-hosting.ts). */
export function legendHostOf(p: SheetElementParts): LegendHostingHost {
  return {
    core: p.core,
    state: p.legendState,
    get handle() { return p.element.handle; },
    get legends() { return p.legends; },
    get legendColumn() { return p.element.$("legend"); },
    get activeRowOptions() { return p.activeRowOptions; },
    get resizeDelegate() { return p.element.legendHandle.resizeDelegate; },
    get pluginManager() { return p.pluginManager; },
    forgetVisibleColumns: () => p.forgetVisibleColumns(),
    get legendCollapsed() { return p.element.legendCollapsed; },
    get legendWidthOverridePx() { return p.element.legendWidthOverridePx; },
    get legendCollapsedWidthPx() { return p.element.legendCollapsedWidthPx; },
    defaultCollapsedLegendPx: p.defaultCollapsedLegendPx,
    get leftOccludedWidthPx() { return p.element.leftOccludedWidthPx; },
    get legendMeasureToast() { return p.element.legendMeasureToast; },
    get legendMeasureSentinel() { return p.element.legendMeasureSentinel; },
    get legendMeasureDebug() { return p.element.legendMeasureDebug; },
    rebindLegendStickyTop: () => p.element.rebindLegendStickyTop(),
    get onTryResize() { return p.element.onTryResize; },
  };
}

/** The deferred emit loop's host (sheet-emit-handle.ts). */
export function emitHostOf(p: SheetElementParts): EmitHandleHost {
  return {
    get sheet() { return p.core.sheet; },
    get layout() { return p.core.layout; },
    state: p.emitState,
    get lastBindStartedAtMs() { return p.bindState.lastBindStartedAtMs; },
    get bindGeneration() { return p.bindState.bindGeneration; },
    get pluginManager() { return p.pluginManager; },
    get legend() { return p.legends.legend; },
    get headerLegend() { return p.legends.headerLegend; },
    get defaultBinder() { return p.defaultBinder; },
    get currentTime() { return p.currentTime; },
    nextSpecGeneration: (key) => p.nextSpecGeneration(key),
    isCurrentSpecGeneration: (key, gen) => p.isCurrentSpecGeneration(key, gen),
    get fixedChildrenLifetime() { return p.fixedChildrenLifetime; },
    get fixedChildrenQueue() { return p.fixedChildrenQueue; },
    get handle() { return p.element.handle; },
    get decorationsHandle() { return p.element.decorationsHandle; },
    get layoutDebugOverlayHandle() { return p.element.layoutDebugOverlayHandle; },
    get fixedChildHandle() { return p.element.fixedChildHandle; },
    get staticChildHandle() { return p.element.staticChildHandle; },
    get stateHandle() { return p.element.stateHandle; },
    get onFocusCell() { return p.element.onFocusCell; },
    get onBlurCell() { return p.element.onBlurCell; },
    get onViewportChanged() { return p.element.onViewportChanged; },
    get resolvedCellWidthPx() { return p.element.resolvedCellWidthPx; },
    assertrl: () => assertrl(),
    residency: () => (DomFlags.read(ROW_DEMAND, false) ? p.rows.emitResidency() : null),
  };
}

/** The decorations' renderer-context host (sheet-decoration-context.ts). */
export function decorationHostOf(p: SheetElementParts): DecorationContextHost {
  return {
    get sheet() { return p.core.sheet; },
    get cellTargets() { return p.cellTargets; },
    get fixedChild() { return p.element.fixedChildHandle; },
  };
}

/** The rebind handle's host (sheet-rebind-handle.ts). */
export function rebindHostOf(p: SheetElementParts): RebindHandleHost {
  return {
    core: p.core,
    state: p.rebindState,
    get root() { return p.$$.root; },
    get legend() { return p.legends.legend; },
    get headerLegend() { return p.legends.headerLegend; },
    get activeRowOptions() { return p.activeRowOptions; },
    get bindReasons() { return p.bindReasons; },
    bindSheet: (sheet, options) => bindSheet(p, sheet, options),
    get composedOptionsRenderer() { return p.composedOptionsRenderer; },
    get currentTime() { return p.currentTime; },
    get defaultBinder() { return p.defaultBinder; },
    get emitHandle() { return p.emitHandle; },
    get pluginManager() { return p.pluginManager; },
    rebindStructurePass: () => rebindStructurePass(p),
    get shouldShowDebugPopover() { return p.shouldShowDebugPopover; },
    suppressForViewModelSwap: (pass) => suppressForViewModelSwap(p, pass),
    get columnRebindToast() { return p.element.columnRebindToast; },
    get handle() { return p.element.handle; },
    get legendHandle() { return p.element.legendHandle; },
    get navigator() { return p.element.navigator; },
    rebindCellHeights: () => p.rebindCellHeights(),
    rebindStaticChildPositions: () => p.rebindStaticChildPositions(),
    rebindFixedChildYPositions: () => p.rebindFixedChildYPositions(),
    get size() { return p.size; },
  };
}

/** The fixed-child handle's host (sheet-fixed-child-handle.ts). */
export function fixedChildHostOf(p: SheetElementParts): FixedChildHandleHost {
  return {
    sheet: () => p.core.sheet,
    children: () => p.core.layout.children,
    bodyFixedChildTop: (offsetPx, location, sheet) => SheetElementLayout.bodyFixedChildTop(offsetPx, location, sheet),
  };
}

/** The static-child handle's host (sheet-static-child-handle.ts). */
export function staticChildHostOf(p: SheetElementParts): StaticChildHandleHost {
  return {
    get sheet() { return p.core.sheet; },
    get children() { return p.core.layout.children; },
    staticChildYPosition: (rowId, rowLayout) => p.cellTargets.staticChildYPosition(rowId, rowLayout),
  };
}

/** The state handle's host (sheet-state-handle.ts). */
export function stateHostOf(p: SheetElementParts): StateHandleHost {
  return {
    get sheet() { return p.core.sheet; },
    get resolvedCellWidth() { return p.element.resolvedCellWidth; },
    get resolvedCellWidthPx() { return p.element.resolvedCellWidthPx; },
    get size() { return p.sizeGate.currentSize; },
    get leftOccludedWidthPx() { return p.element.leftOccludedWidthPx.get(); },
    get rightOccludedWidthPx() { return p.rightOccludedWidthPx; },
    get columns() { return p.core.layout.columns; },
    get currentTime() { return p.currentTime; },
    get scrollPort() { return p.$$.root.scrollPort; },
    get syncStateProvider() { return p.syncStateProvider; },
  };
}

/** The layout debug overlay's switch (`SheetElement.layoutDebugOverlayHandle`); its controller is built on first enable. */
export function layoutDebugOverlayHandleOf(p: SheetElementParts): SheetElement.LayoutDebugOverlayHandle {
  let controller: Option<LayoutDebugOverlayController>;
  const getController = (): LayoutDebugOverlayController => {
    if (!controller) {
      controller = new LayoutDebugOverlayController(p.core.layout, p.element.handle, () => p.core.sheet);
    }
    return controller;
  };
  return {
    setEnabled(enabled: boolean) {
      getController().setEnabled(enabled);
    },
    clear() {
      controller?.clear();
    },
    rebind() {
      controller?.rebind();
    },
  };
}

/** The element's delegate handle (`SheetElement.handle`): its sub-handles, plugins and bind generation. */
export function delegateHandleOf(p: SheetElementParts): SheetDelegate {
  const element = p.element;
  return {
    rebind: element.rebindHandle,
    popover: element.popoverHandle,
    fixedChild: element.fixedChildHandle,
    tooltip: element.tooltipHandle,
    staticChild: element.staticChildHandle,
    state: element.stateHandle,
    get pluginManager() {
      return nonnull(p.pluginManager, "Sheet: plugin manager not installed");
    },
    invalidateStaticChildren() {
      p.emitHandle.reconcileStaticChildren();
    },
    bindGeneration: () => p.bindState.bindGeneration,
  };
}
